import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import { ResponsiveFractalCanvas } from './ResponsiveFractalCanvas';
import { ViewportFractalCanvas } from './ViewportFractalCanvas';
import { closeups } from './fractalPresets';
import { EXPLORER_PALETTE } from './fractalPalette';

// Register only JavaScript for smaller bundle size
hljs.registerLanguage('javascript', javascript);

// Render LaTeX equations using bundled KaTeX.
const Equation = ({ tex }) => {
  const ref = useRef(null);
  
  useEffect(() => {
    if (ref.current) {
      try {
        katex.render(tex, ref.current, {
          throwOnError: false,
          displayMode: true
        });
      } catch (e) {
        console.error('KaTeX render error:', e);
        ref.current.textContent = tex;
      }
    }
  }, [tex]);
  
  return <div className="equation" ref={ref}>{tex}</div>;
}

const CodeBlock = ({ code }) => {
  const trimmed = code.trim();
  const { value: highlighted } = hljs.highlight(trimmed, { language: 'javascript' });
  return (
    <pre>
      <code
        className="language-javascript hljs"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </pre>
  );
};

const ITERATION_PALETTE = { ...EXPLORER_PALETTE, useMu: false };

const App = () => {
  const preferredRenderer = 'webgl';

  return <>
  <header>
    <h1>The Mandelbrot Set</h1>
  </header>

  <section className="section" data-section="intro">
  <div className="container">
    <p>
      If we plot the escape times for points in this complex plane series,
      we end up with one of the most famous visualizations in mathematics.
    </p>
    <Equation tex="z_{n+1} = z_n^2 + c, \quad{i = \sqrt{-1}}" />
    <p>
      The main cardioid is centered near the origin, with the period-2
      bulb to its left at about &minus;1.
    </p>
    <ResponsiveFractalCanvas
      width={1600}
      height={1600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.5, 1.5]}
      palette={EXPLORER_PALETTE}
      preferredRenderer={preferredRenderer}
    />
  </div>
  </section>

  <section className="section" data-section="world-overview">
  <div className="container">
    <h3>World overview</h3>
    <p>
      If we consider an escape radius of 2, the world also has a radius
      of 2, since any points starting outside of the world diverge right away.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={800}
      xRange={[-2.2, 2.2]}
      yRange={[-2.2, 2.2]}
      palette={EXPLORER_PALETTE}
      preferredRenderer={preferredRenderer}
    />

    <p>
      The set lies roughly on the real axis between &minus;2 and 0.5, with
      the imaginary extent reaching about ±1.25.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.25, 1.25]}
      palette={EXPLORER_PALETTE}
      preferredRenderer={preferredRenderer}
    />
  </div>
  </section>

  <section className="section" data-section="substructures">
  <div className="container">
    <h3>Sub-structures</h3>
    <p>
      Zoomed-in to various parts of the Mandelbrot world.
    </p>

    <section className="subsection" data-section="seahorse-valley" aria-labelledby="seahorse-valley-heading">
      <h4 id="seahorse-valley-heading">Seahorse Valley</h4>
      <p>
        A close-up of the curled branches between
        the main cardioid and the period-2 bulb, centred near
        &minus;0.747 + 0.108i.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.seahorse}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="elephant-valley" aria-labelledby="elephant-valley-heading">
      <h4 id="elephant-valley-heading">Elephant Valley</h4>
      <p>
        The region near 0.3 on the real axis,
        with trunk-like spiral structures.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.elephant}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="antenna" aria-labelledby="antenna-heading">
      <h4 id="antenna-heading">Antenna</h4>
      <p>
        The tip of the antenna at &minus;2.0 &mdash; Misiurewicz point.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.antenna}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="feigenbaum-cascade" aria-labelledby="feigenbaum-cascade-heading">
      <h4 id="feigenbaum-cascade-heading">Feigenbaum Cascade</h4>
      <p>
        Follow the shrinking chain of bulbs as their
        periods double from 2 to 4, 8, 16, and beyond. Zoom toward the point
        near &minus;1.401155 on the real axis to explore where the chain accumulates.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.feigenbaum}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="julia-medallion" aria-labelledby="julia-medallion-heading">
      <h4 id="julia-medallion-heading">Double-spiral Julia Medallion</h4>
      <p>
        Near a miniature Mandelbrot's
        Seahorse Valley, curling filaments form a pattern resembling a Julia set.
        Explore the paired spirals and the smaller curls along their edges.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.juliaMedallion}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="elephant-trunk" aria-labelledby="elephant-trunk-heading">
      <h4 id="elephant-trunk-heading">Elephant’s Trunk</h4>
      <p>
        Deeper inside Elephant Valley, long curved
        branches curl into tightly wound tips. Follow a trunk to find smaller
        versions of the same pattern along its sides.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.elephantTrunk}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="double-spiral-valley" aria-labelledby="double-spiral-heading">
      <h4 id="double-spiral-heading">Double Spiral Valley</h4>
      <p>
        On the period-2 bulb side of Seahorse Valley, two intertwined arms
        wind into a shared centre. Zoom in to find smaller curls repeating
        along their branching edges.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.doubleSpiral}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="triple-spiral-valley" aria-labelledby="triple-spiral-heading">
      <h4 id="triple-spiral-heading">Triple Spiral Valley</h4>
      <p>
        Near the upper period-3 bulb, the boundary curls into spirals with
        three arms. Zoom in to follow the smaller spirals along each branch.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.tripleSpiral}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="quad-spiral-valley" aria-labelledby="quad-spiral-heading">
      <h4 id="quad-spiral-heading">Quad Spiral Valley</h4>
      <p>
        Near the upper period-4 bulb, the filaments form spirals with four
        arms. Follow each arm outward to see it branch into smaller spirals,
        adding another turn to the patterns of Double and Triple Spiral Valley.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.quadSpiral}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="mini-mandelbrot" aria-labelledby="mini-mandelbrot-heading">
      <h4 id="mini-mandelbrot-heading">Mini Mandelbrot</h4>
      <p>
        Near &minus;1.75 on the real axis, a miniature Mandelbrot set appears
        among delicate filaments. This period-3 island repeats the familiar
        cardioid and bulbs, with its own valleys waiting to be explored.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.miniMandelbrot}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="scepter-valley" aria-labelledby="scepter-heading">
      <h4 id="scepter-heading">Scepter Valley</h4>
      <p>
        Also known as Seahorse Valley West, this region lies between the
        period-2 and period-4 bulbs. Its curled filaments sprout long,
        branching spikes that resemble ornate scepters.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.scepter}
        preferredRenderer={preferredRenderer}
      />
    </section>

    <section className="subsection" data-section="snowflake" aria-labelledby="snowflake-heading">
      <h4 id="snowflake-heading">Snowflake</h4>
      <p>
        Fine, feathery branches form clusters resembling snowflakes near
        &minus;0.658448 &minus; 0.466852i. Zoom in to find the pattern repeating
        along smaller branches.
      </p>
      <ResponsiveFractalCanvas
        {...closeups.snowflake}
        preferredRenderer={preferredRenderer}
      />
    </section>
  </div>
  </section>

  <section className="section" data-section="equation">
  <div className="container">
    <h3>Mandelbrot set equation</h3>

    <p>The equation using complex numbers is:</p>
    <Equation tex="z_{n+1} = z_n^2 + c" />

    <p>z is a complex number composed of real and imaginary parts:</p>
    <Equation tex="z = x + iy, \quad{i = \sqrt{-1}}" />

    <p>Fractal images are generated by varying the value of c:</p>
    <Equation tex="c = (x_0, y_0)" />

    <p>If we expand the complex squaring, the iteration reduces to:</p>
    <Equation tex="x_{n+1} = x_n^2 - y_n^2 + x_0" />
    <Equation tex="y_{n+1} = 2x_ny_n + y_0" />

    <section>
      <p>
        A common way of exploring fractals is by putting different starting (x0, y0)
        numbers into the equation and making note of whether the series:
      </p>
      <ul>
        <li>diverges to infinity or stays within a limit</li>
        <li>if the series diverges, we make note of:
          <ul>
            <li>the number of iterations until reaching the escape radius</li>
            <li>the distance from zero (0, 0) during the escape</li>
          </ul>
        </li>
      </ul>
    </section>
  </div>
  </section>

  <section className="section" data-section="coloring">
  <div className="container">
    <h3>Fractal coloring</h3>

    <p>
      Escape time: given a starting point (x0, y0), we can give each
      point a color by calculating the number of iterations it takes
      for the series to diverge, and the distance from zero when escaping.
    </p>
    <CodeBlock code={`
const ESCAPE_THRESHOLD = 4;
const MAX_ITERATIONS = 1024;

function iterateUntilEscape(x0, y0) {
  let x = 0, y = 0;
  let numIterations = 0;
  while ((x*x + y*y <= ESCAPE_THRESHOLD) &&
         (numIterations < MAX_ITERATIONS)) {
    const x_next = x*x - y*y + x0;
    const y_next = 2*x*y + y0;
    x = x_next;
    y = y_next;
    numIterations++;
  }
  // Check the last permitted iterate too; radius 2 itself is not escape.
  const escaped = x*x + y*y > ESCAPE_THRESHOLD;
  return [numIterations, x*x + y*y, escaped];
}
`}/>
  </div>
  </section>

  <section className="section" data-section="implementation">
  <div className="container">
    <p>
      We'll set the RGBA colors of every pixel in the image based
      on the number of iterations. Here's an example of a fiery yellow/red
      color palette by scaling just the green and alpha values of each pixel.
    </p>
    <CodeBlock code={`
function getColor(numIterations) {
  return [
    255,                // red
    numIterations * 7,  // green
    0,                  // blue
    numIterations * 15  // alpha
  ];
}
`}/>

    <p>
      Here's a general function for drawing fractals onto canvas elements.
      Changing the iterateUntilEscape function will change the drawn fractal.
    </p>

    <CodeBlock code={`
function drawFractal(canvasEl, xRange, yRange, getColor) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const canvasImageData =
    context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  for (let i = 0; i < canvasHeight; i++) {
    for (let j = 0; j < canvasWidth; j++) {
      const x0 = xRange[0] +
        j*(xRange[1] - xRange[0]) / canvasWidth;
      const y0 = yRange[0] +
        i*(yRange[1] - yRange[0]) / canvasHeight;
      const [numIterations, escapeDistance, escaped] =
        iterateUntilEscape(x0, y0);
      const pixelIndex = 4*i*canvasWidth + 4*j;
      if (escaped) {
        const mu = getMu(numIterations, escapeDistance);
        const pixels = getColor({ numIterations, mu });
        for (let p = 0; p < 4; p++) {
          image[pixelIndex + p] = pixels[p];
        }
      }
    }
  }
  context.putImageData(canvasImageData, 0, 0);
}`
}/>

    <p>
      Here's the main cardioid colored based on
      number of iterations alone.
    </p>
    <ResponsiveFractalCanvas
      width={1600}
      height={1600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.5, 1.5]}
      palette={ITERATION_PALETTE}
      preferredRenderer={preferredRenderer}
    />
    <p>
      By considering escape distances on a logarithmic scale, we
      can use this extra information to smooth the color transitions.
    </p>
    <CodeBlock code={`
const ESCAPE_RADIUS = 2;

function getMu(numIterations, distanceSquared) {
  return numIterations + 1 -
    Math.log(0.5 * Math.log(distanceSquared)) /
    Math.log(ESCAPE_RADIUS);
}

function getColor(mu) {
  return [ 255, mu * 7, 0, mu * 15 ];
}
`}/>
    <p>
      Here's the same image with continuous escape counts and a smooth, repeating
      navy, teal, ivory, and amber palette. Extra iterations after escape soften
      contour seams, while a 1,024-iteration budget reveals finer boundary detail.
    </p>
    <ResponsiveFractalCanvas
      width={1600}
      height={1600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.5, 1.5]}
      palette={EXPLORER_PALETTE}
      preferredRenderer={preferredRenderer}
    />
    <p>
      Nebulabrots are a way of rendering fractals where pixels are colored based
      on how frequently they're visited while calculating the iterations until
      escape across the viewport.
    </p>
  </div>
  </section>

    <section>
      <div className="container">
        <h3>About</h3>
        <p>
          All fractals on the page are interactive and rendered when they're in view.
          The renderer uses WebGL by default for high performance, falling back to canvas if WebGL is not available.
        </p>
        <p>Fractal image controls when hovering</p>
        <ul>
          <li>shift + scroll mouse wheel — zoom in/out</li>
          <li>pinch — zoom in/out</li>
          <li>click and drag — pan the view</li>
          <li>cmd-z — undo the last camera action</li>
        </ul>

        <p>TODO</p>
        <ul>
          <li>Plot more areas of the Mandelbrot set</li>
          <li>Improve coloring code for better dynamic range</li>
          <li>Enable customizing colors</li>
          <li>Enable resetting each canvas to its original state</li>
          <li>Allow editing x and y ranges: text inputs or click/drag slider</li>
          <li>Maybe show the (x0, y0) center of each canvas</li>
          <li>Draw boxes when selecting parts of the canvas to zoom in on</li>
        </ul>
      </div>
    </section>

    <footer>
      <div className="container">
        <h2>References</h2>
        <ul>
          <li><a href="https://en.wikipedia.org/wiki/Mandelbrot_set">Wikipedia - Mandelbrot set</a></li>
        </ul>
      </div>
    </footer>

    <ViewportFractalCanvas preferredRenderer={preferredRenderer} />
</>;
};

export default App;
